import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout, ActionButton } from './DesignSystem';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';
import { showToast } from '../utils/toast';

const UK_POSTCODE_RE = /^([A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}|GIR\s?0AA)$/i;
const UK_VAT_RE = /^(GB)?(\d{9}|\d{12}|(GD|HA)\d{3})$/i;
const UK_SORT_CODE_RE = /^\d{6}$/;

const SUPPLIER_CATEGORIES = [
  'Grocery & Food',
  'Beverages & Drinks',
  'Frozen & Chilled',
  'Household & Cleaning',
  'Packaging & Disposables',
  'Equipment & Services',
  'Other',
];

const PAYMENT_TERMS = ['Net 7', 'Net 14', 'Net 30', 'Net 60', 'Net 90', 'Due on Receipt', 'Advance Payment'];

const s = {
  form: { maxWidth: 1200, margin: '0 auto' } as React.CSSProperties,
  section: {
    background: 'white', padding: 24, borderRadius: 12, marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#111827',
    borderBottom: '2px solid #e5e7eb', paddingBottom: 12,
  } as React.CSSProperties,
  r: { display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' as const } as React.CSSProperties,
  c: { flex: '1 1 280px', minWidth: 0 } as React.CSSProperties,
  l: { display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14, color: '#374151' } as React.CSSProperties,
  i: {
    width: '100%', padding: '10px 14px', border: '1.5px solid #d1d5db',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  hint: { fontSize: 12, color: '#9ca3af', marginTop: 4 } as React.CSSProperties,
  actions: { display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 } as React.CSSProperties,
  submitBtn: {
    padding: '12px 32px', background: '#3b82f6', color: 'white', border: 'none',
    borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
  } as React.CSSProperties,
  cancelBtn: {
    padding: '12px 32px', background: 'white', color: '#6b7280',
    border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
  } as React.CSSProperties,
  alert: {
    padding: 14, background: '#eff6ff', border: '1px solid #bfdbfe',
    borderRadius: 8, marginBottom: 20, color: '#1e40af', fontSize: 14,
  } as React.CSSProperties,
};

function buildPayload(form: Record<string, unknown>, image: string) {
  const taxRaw = String(form.taxId || '').trim().replace(/\s/g, '').toUpperCase();
  const taxId = taxRaw ? (taxRaw.startsWith('GB') ? taxRaw : `GB${taxRaw}`) : '';
  return {
    name: String(form.name || '').trim(),
    company: String(form.company || '').trim(),
    contactPerson: String(form.contactPerson || '').trim(),
    contact_person: String(form.contactPerson || '').trim(),
    email: String(form.email || '').trim(),
    phone: String(form.phone || '').trim(),
    address: String(form.address || '').trim(),
    city: String(form.city || '').trim(),
    postcode: String(form.postcode || '').trim().toUpperCase(),
    country: String(form.country || 'United Kingdom').trim(),
    category: String(form.category || '').trim(),
    website: String(form.website || '').trim(),
    taxId,
    tax_id: taxId,
    paymentTerms: form.paymentTerms || 'Net 30',
    payment_terms: form.paymentTerms || 'Net 30',
    status: form.status || 'Active',
    notes: String(form.notes || '').trim(),
    image,
    logo: image,
    preferredSupplier: !!form.preferred,
    preferred_supplier: !!form.preferred,
    bankSortCode: String(form.sortCode || '').trim(),
    bank_sort_code: String(form.sortCode || '').trim(),
    bankAccountNumber: String(form.accountNumber || '').trim(),
    bank_account_number: String(form.accountNumber || '').trim(),
    reorderThreshold: Number(form.reorderThreshold || 0),
    reorder_threshold: Number(form.reorderThreshold || 0),
    leadTimeDays: Number(form.leadTimeDays || 0),
    lead_time_days: Number(form.leadTimeDays || 0),
  };
}

const SupplierForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEdit = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [attachments, setAttachments] = useState<{ id: number; filename: string; url: string }[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [form, setForm] = useState({
    name: '',
    company: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    category: '',
    website: '',
    taxId: '',
    paymentTerms: 'Net 30',
    status: 'Active' as 'Active' | 'Inactive' | 'Pending' | 'Blocked',
    notes: '',
    preferred: false,
    sortCode: '',
    accountNumber: '',
    reorderThreshold: '',
    leadTimeDays: '',
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    if (!token) {
      showToast('Please log in to edit suppliers', 'warning');
      navigate('/login');
      return;
    }
    setLoading(true);
    apiFetch(`${API_URL}/suppliers/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async res => {
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.json();
      })
      .then(async data => {
        let img = data.image || data.logo || '';
        if (!img && (data.has_logo || data.hasLogo)) {
          try {
            const logoRes = await apiFetch(`${API_URL}/suppliers/${id}/logo`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (logoRes.ok) {
              const logoData = await logoRes.json();
              img = logoData.image || '';
            }
          } catch { /* optional */ }
        }
        setForm({
          name: data.name || '',
          company: data.company || '',
          contactPerson: data.contactPerson || data.contact_person || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          postcode: data.postcode || '',
          country: data.country || 'United Kingdom',
          category: data.category || '',
          website: data.website || '',
          taxId: data.taxId || data.tax_id || '',
          paymentTerms: data.paymentTerms || data.payment_terms || 'Net 30',
          status: data.status || 'Active',
          notes: data.notes || '',
          preferred: !!(data.preferredSupplier || data.preferred_supplier),
          sortCode: data.bankSortCode || data.bank_sort_code || '',
          accountNumber: data.bankAccountNumber || data.bank_account_number || '',
          reorderThreshold: data.reorderThreshold != null ? String(data.reorderThreshold) : (data.reorder_threshold != null ? String(data.reorder_threshold) : ''),
          leadTimeDays: data.leadTimeDays != null ? String(data.leadTimeDays) : (data.lead_time_days != null ? String(data.lead_time_days) : ''),
        });
        setImagePreview(img);
        fetchAttachments();
      })
      .catch(() => {
        showToast('Failed to load supplier', 'error');
        navigate('/suppliers/list');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, token, navigate]);

  const fetchAttachments = async () => {
    if (!id || !token) return;
    try {
      const res = await apiFetch(`${API_URL}/suppliers/${id}/attachments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAttachments(await res.json());
    } catch { /* ignore */ }
  };

  const uploadAttachment = async (file: File) => {
    if (!id || !token) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiFetch(`${API_URL}/suppliers/${id}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (res.ok) {
      showToast('Attachment uploaded', 'success');
      await fetchAttachments();
    } else {
      showToast('Failed to upload attachment', 'error');
    }
  };

  const deleteAttachment = async (attId: number) => {
    if (!id || !token) return;
    const res = await apiFetch(`${API_URL}/suppliers/${id}/attachments/${attId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } else {
      showToast('Failed to delete attachment', 'error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be under 2 MB', 'warning');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    if (!form.name.trim()) { showToast('Supplier name is required', 'warning'); return false; }
    if (!form.company.trim()) { showToast('Company name is required', 'warning'); return false; }
    if (!form.email.trim()) { showToast('Email is required', 'warning'); return false; }
    if (!form.phone.trim()) { showToast('Phone is required', 'warning'); return false; }
    if (!form.category.trim()) { showToast('Category is required', 'warning'); return false; }
    if (form.postcode.trim() && !UK_POSTCODE_RE.test(form.postcode.trim())) {
      showToast('Enter a valid UK postcode (e.g. SW1A 1AA)', 'warning');
      return false;
    }
    const vat = form.taxId.trim().replace(/\s/g, '').toUpperCase();
    if (vat && !UK_VAT_RE.test(vat)) {
      showToast('Enter a valid UK VAT number (e.g. GB123456789)', 'warning');
      return false;
    }
    const sortDigits = form.sortCode.replace(/\D/g, '');
    if (form.sortCode.trim() && !UK_SORT_CODE_RE.test(sortDigits)) {
      showToast('Enter a valid UK sort code (6 digits, e.g. 12-34-56)', 'warning');
      return false;
    }
    return true;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!token) { navigate('/login'); return; }

    setSaving(true);
    try {
      const payload = buildPayload(form, imagePreview);
      const url = isEdit ? `${API_URL}/suppliers/${id}` : `${API_URL}/suppliers`;
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const supplierId = isEdit ? id : (data.id || data._id);

        if (!isEdit && pendingFiles.length > 0 && supplierId) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            await apiFetch(`${API_URL}/suppliers/${supplierId}/attachments`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
          }
        }

        showToast(isEdit ? 'Supplier updated successfully' : 'Supplier created successfully', 'success');
        navigate(supplierId ? `/suppliers/view/${supplierId}` : '/suppliers/list', {
          state: isEdit ? undefined : { flash: 'Supplier created successfully' },
        });
      } else {
        showToast(data.message || data.error || 'Failed to save supplier', 'error');
      }
    } catch {
      showToast('Network error — check your connection', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center' }}>Loading supplier…</div>;
  }

  const initial = (form.name || '?')[0]?.toUpperCase();

  return (
    <PageLayout
      title={isEdit ? '✏️ Edit Supplier' : '➕ Add Supplier'}
      action={
        <ActionButton
          variant="add"
          label={saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Supplier'}
          onClick={() => (document.getElementById('supplier-form') as HTMLFormElement)?.requestSubmit()}
        />
      }
    >
      <form id="supplier-form" onSubmit={onSubmit} style={s.form}>
        <div style={s.alert}>
          UK wholesale supplier record — fields marked <strong>*</strong> are required. Payment terms and lead time feed into purchase orders.
        </div>

        {/* Basic info */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Basic Information</h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            {imagePreview ? (
              <img src={imagePreview} alt="preview" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb' }} />
            ) : (
              <div style={{
                width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 24,
              }}>{initial}</div>
            )}
            <div>
              <button type="button" onClick={() => fileInputRef.current?.click()} style={{ ...s.cancelBtn, padding: '8px 16px', marginBottom: 6 }}>
                Upload logo
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFile} />
              <div style={s.hint}>JPG, PNG — max 2 MB. Or paste a URL below.</div>
            </div>
          </div>
          <div style={{ ...s.r, marginBottom: 16 }}>
            <div style={{ ...s.c, flex: '1 1 100%' }}>
              <label style={s.l}>Logo URL</label>
              <input
                name="imageUrl"
                value={imagePreview.startsWith('data:') ? '' : imagePreview}
                onChange={e => setImagePreview(e.target.value)}
                style={s.i}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>

          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Supplier Name *</label>
              <input name="name" value={form.name} onChange={handleChange} style={s.i} placeholder="ABC Wholesale Ltd" required />
            </div>
            <div style={s.c}>
              <label style={s.l}>Company / Trading Name *</label>
              <input name="company" value={form.company} onChange={handleChange} style={s.i} placeholder="ABC Foods Ltd" required />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Contact Person</label>
              <input name="contactPerson" value={form.contactPerson} onChange={handleChange} style={s.i} placeholder="Primary buyer contact" />
            </div>
            <div style={s.c}>
              <label style={s.l}>Category *</label>
              <select name="category" value={form.category} onChange={handleChange} style={s.i} required>
                <option value="">Select category…</option>
                {SUPPLIER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} style={s.i} placeholder="orders@supplier.co.uk" required />
            </div>
            <div style={s.c}>
              <label style={s.l}>Phone *</label>
              <input name="phone" value={form.phone} onChange={handleChange} style={s.i} placeholder="+44 20 1234 5678" required />
            </div>
          </div>
        </div>

        {/* Address */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Address</h2>
          <div style={{ ...s.r, marginBottom: 0 }}>
            <div style={{ ...s.c, flex: '1 1 100%' }}>
              <label style={s.l}>Street Address</label>
              <textarea name="address" value={form.address} onChange={handleChange} style={{ ...s.i, minHeight: 72, resize: 'vertical' }} placeholder="Unit 4, Industrial Estate" />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>City</label>
              <input name="city" value={form.city} onChange={handleChange} style={s.i} placeholder="London" />
            </div>
            <div style={s.c}>
              <label style={s.l}>Postcode</label>
              <input name="postcode" value={form.postcode} onChange={handleChange} style={s.i} placeholder="SW1A 1AA" />
            </div>
            <div style={s.c}>
              <label style={s.l}>Country</label>
              <input name="country" value={form.country} onChange={handleChange} style={s.i} />
            </div>
          </div>
        </div>

        {/* Business */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Business &amp; Payment</h2>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Website</label>
              <input name="website" value={form.website} onChange={handleChange} style={s.i} placeholder="https://supplier.co.uk" />
            </div>
            <div style={s.c}>
              <label style={s.l}>VAT Number</label>
              <input name="taxId" value={form.taxId} onChange={handleChange} style={s.i} placeholder="GB123456789" />
              <div style={s.hint}>UK VAT registration — validated on save</div>
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Bank Sort Code</label>
              <input name="sortCode" value={form.sortCode} onChange={handleChange} style={s.i} placeholder="12-34-56" />
            </div>
            <div style={s.c}>
              <label style={s.l}>Bank Account Number</label>
              <input name="accountNumber" value={form.accountNumber} onChange={handleChange} style={s.i} placeholder="12345678" />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Payment Terms</label>
              <select name="paymentTerms" value={form.paymentTerms} onChange={handleChange} style={s.i}>
                {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={s.c}>
              <label style={s.l}>Lead Time (days)</label>
              <input type="number" min={0} name="leadTimeDays" value={form.leadTimeDays} onChange={handleChange} style={s.i} placeholder="e.g. 3" />
            </div>
            <div style={s.c}>
              <label style={s.l}>Reorder Threshold (units)</label>
              <input type="number" min={0} name="reorderThreshold" value={form.reorderThreshold} onChange={handleChange} style={s.i} placeholder="e.g. 10" />
            </div>
          </div>
          <div style={s.r}>
            <div style={s.c}>
              <label style={s.l}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} style={s.i}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>
            <div style={{ ...s.c, display: 'flex', alignItems: 'center', paddingTop: 28 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                <input type="checkbox" name="preferred" checked={form.preferred} onChange={handleChange} />
                Preferred supplier
              </label>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Notes</h2>
          <textarea name="notes" value={form.notes} onChange={handleChange} style={{ ...s.i, minHeight: 100, resize: 'vertical' }} placeholder="Delivery windows, minimum order values, account manager…" />
        </div>

        {/* Attachments — price lists, delivery specs, contracts */}
        <div style={{ ...s.section, background: '#f9fafb' }}>
          <h2 style={s.sectionTitle}>📎 Attachments</h2>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: -12, marginBottom: 16 }}>
            Upload price lists, delivery schedules, or supplier contracts (PDF, Excel, images — max 5 MB).
          </p>
          {isEdit && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {attachments.length === 0 && <span style={{ fontSize: 13, color: '#888' }}>No attachments yet.</span>}
              {attachments.map(att => (
                <div key={att.id} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d1d5db' }}>
                  <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#3b82f6' }}>📄 {att.filename}</a>
                  <button type="button" onClick={() => deleteAttachment(att.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          {!isEdit && pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {pendingFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} style={{ background: '#fff', padding: '6px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d1d5db' }}>
                  <span style={{ fontSize: 13 }}>📄 {f.name}</span>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <div>
            <input
              type="file"
              id="supplier-attachment-input"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                if (file.size > 5 * 1024 * 1024) {
                  showToast('File must be under 5 MB', 'warning');
                  return;
                }
                if (isEdit) {
                  setUploading(true);
                  uploadAttachment(file).finally(() => setUploading(false));
                } else {
                  setPendingFiles(prev => [...prev, file]);
                }
              }}
            />
            <label htmlFor="supplier-attachment-input" style={{
              display: 'inline-block', background: uploading ? '#9ca3af' : '#3b82f6', color: 'white',
              padding: '8px 18px', borderRadius: 6, cursor: uploading ? 'wait' : 'pointer', fontWeight: 600, fontSize: 14,
            }}>
              {uploading ? 'Uploading…' : 'Upload file'}
            </label>
          </div>
        </div>

        <div style={s.actions}>
          <button type="button" style={s.cancelBtn} onClick={() => navigate(isEdit ? `/suppliers/view/${id}` : '/suppliers/list')} disabled={saving}>
            Cancel
          </button>
          <button type="submit" style={s.submitBtn} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Supplier'}
          </button>
        </div>
      </form>
    </PageLayout>
  );
};

export default SupplierForm;
